import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Button, Checkbox, Chip, Tooltip, Typography, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import cuid from "cuid";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import FleetMessageRecipients from "@/components/FleetMessageRecipients.tsx";
import FleetMessageSuccess from "@/components/FleetMessageSuccess.tsx";
import GeoTargetRecipients from "@/components/GeoTargetRecipients";
import PaperAirplane from "@/components/Icons/PaperAirplane";
import ListTemplates from "@/components/ListTemplates";
import Loading from "@/components/Loading/Index.tsx";
import TextMessageInput from "@/components/TextMessageInput";
import { ampli } from "@/config";
import TusHooks from "@/hooks/UseTusHooks/TusHooks.tsx";
import Locator from "@/locator";
import { ActionContext } from "@/models/ActionsProvider";
import { bootstrapWorkspaceFeedPermissions, loadBootstrapEventAndHydrateLocalDB } from "@/models/BootstrapUtils.ts";
import { attachmentPanel } from "@/models/fleetMessageAttachment";
import { DuplicateAliasAccount } from "@/models/repositories";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { UxContext } from "@/models/UxStateProvider";
import { recordSpan } from "@/services/telemetry";
import type { FleetMessageRecipientMode } from "@/types/fleetMessage";
import { pluralValue } from "@/utils";
import { checkForDuplicateAccounts } from "@/utils/feed.ts";
import ButtonTabs from "./ButtonTabs";

export default function FleetMessaging({
  closeModal,
  fleetMessageText,
  setFleetMessageText,
}: {
  closeModal: () => void;
  fleetMessageText: string;
  setFleetMessageText: (value: string) => void;
}) {
  const {
    fleetMessageInvalidLocation,
    fleetMessageActiveChannels,
    fleetMessageActiveSection,
    setFleetMessageLatitude,
    setFleetMessageLongitude,
    setFleetMessageLocation,
    setFleetMessageActiveChannels,
    fleetMessageTableSelection,
    setFleetMessageTableSelection,
    fleetMessageContentId,
    setFleetMessageContentId,
    fleetMessageSendSilently,
    setFleetMessageSendSilently,
    fleetMessageRecipientMode,
    setFleetMessageRecipientMode,
    setFleetMessageText: persistFleetMessageText,
    returnToDriverTable,
  } = useContext(UxContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const { publishBroadcast } = useContext(ActionContext);
  const theme = useTheme();
  const [disabledButton, setDisabledButton] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showTemplates, setShowTemplates] = useState<boolean>(false);
  const [showUpload, setShowUpload] = useState<boolean>(!!fleetMessageContentId);
  const [serverError, setServerError] = useState<string[]>([]);
  const [temporaryChannels, setTemporaryChannels] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>(fleetMessageActiveSection ?? "select-recipients");
  // An attachment that survived a trip to the driver table is already uploaded; remounting the uploader over it
  // would reopen the file chooser, so the pane shows the attachment instead until Remove File clears it.
  const [restoredContentId] = useState<string | null>(fleetMessageContentId);

  useEffect(() => {
    if (fleetMessageActiveSection) {
      setActiveTab(fleetMessageActiveSection);
    }
  }, [fleetMessageActiveSection]);

  // Perceived fleet-message UI load: modal mount to the next painted frame.
  useEffect(() => {
    const openedAt = new Date();
    const frame = requestAnimationFrame(() => {
      recordSpan("fleet_message.ui_load", openedAt);
    });
    return () => cancelAnimationFrame(frame);
  }, []);
  const [hasDuplicatesMessage, setHasDuplicatesMessage] = useState<boolean>(false);
  const [hasDuplicates, setHasDuplicates] = useState<DuplicateAliasAccount[]>([]);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const handleUploadContent = useCallback(
    (value: { itemId: string; contentId: string }) => {
      setFleetMessageContentId(value.contentId);
    },
    [setFleetMessageContentId],
  );

  const toggleShowTemplates = () => {
    setShowTemplates((value) => !value);
    setShowUpload(() => false);
    if (!showTemplates) {
      ampli.openTemplatesFleetMessage();
    }
  };

  const resetAllFleetValues = () => {
    setFleetMessageActiveChannels(() => []);
    setFleetMessageLatitude(() => "");
    setFleetMessageLongitude(() => "");
    setFleetMessageText("");
    persistFleetMessageText(() => "");
    setFleetMessageLocation(() => "");
    setFleetMessageTableSelection(() => []);
    setFleetMessageRecipientMode(() => null);
    setFleetMessageSendSilently(() => false);
  };

  useEffect(() => {
    if (!workspaceId) {
      return;
    }

    let isCancelled = false;

    checkForDuplicateAccounts({
      feedIds: fleetMessageActiveChannels,
      workspaceId,
    }).then((data) => {
      if (!isCancelled) {
        setHasDuplicatesMessage(() => data.length > 0);
        setHasDuplicates(() => data);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [fleetMessageActiveChannels, workspaceId]);

  useEffect(() => {
    if (!workspaceId) {
      return;
    }

    let isCancelled = false;

    setLoading(true);
    loadBootstrapEventAndHydrateLocalDB({
      workspaceId,
      bootstrapEvent: bootstrapWorkspaceFeedPermissions,
      pageSize: 10000,
      page: 0,
    })
      .then(() => {
        if (!isCancelled) {
          setError(false);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [workspaceId]);

  const handleRecipientMode = useCallback(
    (mode: FleetMessageRecipientMode, feedIds: string[]) => {
      setFleetMessageRecipientMode(() => mode);
      setFleetMessageActiveChannels(feedIds);
    },
    [setFleetMessageRecipientMode, setFleetMessageActiveChannels],
  );

  // Leaving for the table is not a discard: the body, its attachment and the chosen option stay in context so the
  // Fleet Message action on the table reopens the same draft (WEB-2272).
  const handleGoToDriverTable = useCallback(() => {
    persistFleetMessageText(fleetMessageText);
    returnToDriverTable();
  }, [fleetMessageText, persistFleetMessageText, returnToDriverTable]);

  const tabs = useMemo(
    () =>
      [
        {
          key: "select-recipients",
          icon: <CheckBoxIcon />,
          component: (
            <FleetMessageRecipients
              mode={fleetMessageRecipientMode}
              onModeChange={handleRecipientMode}
              tableSelection={fleetMessageTableSelection}
              activeChannels={fleetMessageActiveChannels}
              onGoToDriverTable={handleGoToDriverTable}
            />
          ),
          text: Locator.feed.input.fleetMessage.modal.selectRecipients,
        },
        {
          key: "geo-target",
          icon: <AddLocationAltIcon />,
          component: (
            <Box>
              <GeoTargetRecipients fleetMessageInvalidLocation={fleetMessageInvalidLocation} />
            </Box>
          ),
          text: Locator.feed.input.fleetMessage.modal.geoLocation,
        },
      ].map((tab) => ({ ...tab, active: tab.key === activeTab })),
    [
      fleetMessageActiveChannels,
      fleetMessageRecipientMode,
      fleetMessageTableSelection,
      fleetMessageInvalidLocation,
      activeTab,
      handleRecipientMode,
      handleGoToDriverTable,
    ],
  );

  const activeComponent = tabs.find((state) => state.active)?.component;
  const activeTabKey = tabs?.find((tab) => tab.active)?.key;

  const resetAfterSubmit = () => {
    ampli.sendFleetMessage({ source: activeTabKey });
    setFleetMessageContentId(() => null);
    setShowSuccessMessage(() => true);
    setTemporaryChannels(() => fleetMessageActiveChannels);
    setFleetMessageActiveChannels(() => []);
    setFleetMessageText("");
    persistFleetMessageText(() => "");
    setDisabledButton(() => false);
    setShowUpload(() => false);
    setServerError(() => []);
    setFleetMessageSendSilently(() => false);
  };

  /** Send round-trip (send → server ack) as a perceived-UX span, tagged success/failure. */
  const publishBroadcastWithTelemetry = async (payload: Parameters<typeof publishBroadcast>[0]) => {
    const sendStartedAt = new Date();
    const attributes = {
      fleetMessage: {
        recipients: fleetMessageActiveChannels?.length ?? 0,
        source: activeTabKey,
        hasAttachment: !!fleetMessageContentId,
        isSilent: fleetMessageSendSilently,
      },
    };
    try {
      const result = await publishBroadcast(payload);
      recordSpan("fleet_message.send", sendStartedAt, attributes);
      return result;
    } catch (e) {
      recordSpan("fleet_message.send_failed", sendStartedAt, attributes, "reliability", { isError: true });
      throw e;
    }
  };

  const errorOverride = (code: number, message: string): string[] => {
    if (code === 403) {
      return [
        "Fleet message failed to send.",
        "Please refresh page and try again.",
        "Contact support if you continue to experience issues  +1 (844) 619-0500.",
      ];
    }
    if (!message) {
      return ["An error occurred while sending a fleet message."];
    }
    return [message];
  };

  const submitWithAttachment = async () => {
    if (!fleetMessageActiveChannels || fleetMessageActiveChannels?.length === 0) {
      setServerError(() => ["Please add at least one recipient before sending."]);
      return;
    }
    setDisabledButton(() => true);

    try {
      const payload = {
        workspaceId,
        feedIds: fleetMessageActiveChannels,
        contentId: fleetMessageContentId,
        isSilent: fleetMessageSendSilently,
      };
      await publishBroadcastWithTelemetry(payload);
      resetAfterSubmit();
    } catch (e) {
      setServerError(() => errorOverride(e?.status, e?.message));
      setDisabledButton(() => false);
      setFleetMessageContentId(() => null);
      return Promise.reject(e);
    }
  };

  const submitForm = async () => {
    if (!fleetMessageActiveChannels || fleetMessageActiveChannels?.length === 0) {
      setServerError(() => ["Please add at least one recipient before sending."]);
      return;
    }
    setDisabledButton(() => true);
    const payload = {
      workspaceId,
      contentId: cuid(),
      feedIds: fleetMessageActiveChannels,
      inputText: fleetMessageText,
      isSilent: fleetMessageSendSilently,
    };

    try {
      await publishBroadcastWithTelemetry(payload);
      resetAfterSubmit();
    } catch (e) {
      setServerError(() => errorOverride(e?.status, e?.message));
      setDisabledButton(() => false);
      return Promise.reject(e);
    }
  };

  const disabledState = () => {
    const missingActiveChannels = fleetMessageActiveChannels?.length === 0;
    const tooManyCharacters = !showUpload && fleetMessageText?.length >= 5000;
    const noFleetText = !showUpload && fleetMessageText?.length <= 0;

    if (disabledButton) {
      return true;
    }

    if (missingActiveChannels) {
      return true;
    }
    if (tooManyCharacters) {
      return true;
    }
    if (noFleetText) {
      return true;
    }
    return showUpload && !fleetMessageContentId;
  };

  const disabledValue = disabledState();

  const messageLabelText = `${
    fleetMessageActiveChannels?.length
  } Recipient${pluralValue(fleetMessageActiveChannels?.length)}`;

  const disabledAddFileButton = showTemplates || fleetMessageText?.length > 0 || disabledButton;

  const uploadPanel = attachmentPanel({
    contentId: fleetMessageContentId,
    restoredContentId,
    hasServerError: serverError.length > 0,
  });

  const tabReset = () => {
    setFleetMessageActiveChannels([]);
    setFleetMessageLocation("");
    setFleetMessageRecipientMode(() => null);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "300px",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Box>Loading Permissions</Box>
        <Loading variant="white" size="large" baseSize={6} />
      </Box>
    );
  } else if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "300px",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ mb: 2 }}>Error Loading Permissions</Box>
          <Button onClick={() => window.location.reload()}>Click To Refresh</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "300px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {showSuccessMessage ? (
        <FleetMessageSuccess
          temporaryChannels={temporaryChannels}
          setShowSuccessMessage={(value: boolean) => {
            setShowSuccessMessage(() => value);
            resetAllFleetValues();
          }}
        />
      ) : (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", gap: 2, height: "450px" }}>
            <Box sx={{ width: "40%", position: "relative" }}>
              {hasDuplicatesMessage && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 96,
                    borderRadius: "0.5rem",
                    alignItems: "center",
                    width: "calc(100% - 8px)",
                    left: 4,
                    right: 0,
                    background: theme.palette.error.light,
                    p: 2,
                    pr: 4,
                    zIndex: 10,
                    maxHeight: "322px",
                    overflowY: "auto",
                    display: "block",
                  }}
                >
                  <IconButton
                    sx={{ position: "absolute", top: 4, right: 4 }}
                    size="small"
                    aria-label="close"
                    color="primary"
                    onClick={(e) => setHasDuplicatesMessage(() => false)}
                  >
                    <CloseIcon role="img" />
                  </IconButton>
                  <Box sx={{ mb: 1 }}>
                    The following ({hasDuplicates?.length}) drivers will receive the message more than once if you
                    proceed with this selection:{" "}
                  </Box>
                  <Box
                    sx={{
                      fontSize: "12px",
                    }}
                  >
                    {hasDuplicates.map((dupe) => dupe.name).join(", ")}
                  </Box>
                </Box>
              )}
              {serverError?.length > 0 && (
                <Box
                  sx={{
                    borderRadius: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    position: "absolute",
                    background: theme.palette.error.light,
                    textAlign: "center",
                    py: 6,
                    px: 2,
                  }}
                >
                  <IconButton
                    sx={{ position: "absolute", top: 4, right: 4 }}
                    size="small"
                    aria-label="close"
                    color="primary"
                    onClick={(e) => setServerError(() => [])}
                  >
                    <CloseIcon role="img" />
                  </IconButton>
                  <Box>
                    {serverError?.map((error) => (
                      <Box sx={{ mb: 1 }}>{error}</Box>
                    ))}
                  </Box>
                </Box>
              )}
              {!showUpload ? (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, whiteSpace: "nowrap" }}>
                    <Typography
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      Message
                    </Typography>

                    {messageLabelText && (
                      <Chip
                        data-sb={TestIds.fleet.recipientCount}
                        label={messageLabelText}
                        size="small"
                        sx={{
                          background:
                            fleetMessageActiveChannels?.length > 0
                              ? theme.palette.success.light
                              : theme.palette.secondary.light,
                        }}
                      />
                    )}
                    <Box
                      component="label"
                      data-sb={TestIds.fleet.silent}
                      sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                    >
                      <Checkbox
                        size="small"
                        sx={{ p: 0.5 }}
                        checked={fleetMessageSendSilently}
                        onChange={(event) => setFleetMessageSendSilently(() => event.target.checked)}
                        inputProps={{
                          "aria-label": Locator.feed.input.fleetMessage.modal.sendSilently,
                        }}
                      />
                      <Typography sx={{ fontSize: "14px" }}>Send Silently</Typography>
                    </Box>
                    <Tooltip title="Message will not play automatically upon receipt. Drivers will still be notified that they have a message waiting for them. Use this for long messages that can be read or listened to when they're not driving.">
                      <HelpOutlineIcon tabIndex={0} sx={{ fontSize: 16, ml: -0.5 }} />
                    </Tooltip>
                  </Box>
                  <Box data-sb={TestIds.fleet.messageInput}>
                    <TextMessageInput
                      rows={13}
                      textInputValue={fleetMessageText}
                      textInputHandler={(e) => setFleetMessageText(e)}
                      showHint={false}
                      required={true}
                      aria-label={Locator.feed.input.fleetMessage.modal.tts}
                    />
                  </Box>
                </>
              ) : (
                <Box sx={{ height: "calc(100% - 69px)", mb: 2 }}>
                  {uploadPanel === "restored" && (
                    <Box
                      sx={{
                        display: "flex",
                        border: `dashed 2px ${theme.palette.secondary.light}`,
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "1rem",
                        minHeight: "120px",
                        p: 2,
                      }}
                    >
                      Your attachment is ready to send. Remove File to pick a different one.
                    </Box>
                  )}
                  {uploadPanel === "uploader" && <TusHooks autoClick={true} setUploadContent={handleUploadContent} />}
                </Box>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  data-sb={TestIds.fleet.templates}
                  disabled={showUpload}
                  variant={showTemplates ? "contained" : "outlined"}
                  sx={{ display: "flex", gap: 2 }}
                  onClick={toggleShowTemplates}
                >
                  <span>Templates</span>
                  {showTemplates && (
                    <Box sx={{ display: "flex" }}>
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Box>
                  )}
                </Button>
                <Button
                  disabled={disabledAddFileButton}
                  variant={showUpload ? "contained" : "outlined"}
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                  onClick={() => {
                    setShowUpload((value) => !value);
                    setShowTemplates(() => false);
                    // The silent-send checkbox is only visible in text mode, so
                    // clear it when entering upload mode to avoid a hidden flag.
                    setFleetMessageSendSilently(() => false);
                    if (fleetMessageContentId) {
                      setFleetMessageContentId(() => null);
                    }
                  }}
                >
                  {fleetMessageContentId ? <span>Remove File</span> : <span>Add File</span>}

                  {showUpload && (
                    <Box sx={{ display: "flex" }}>
                      <CloseIcon sx={{ fontSize: 15 }} />
                    </Box>
                  )}
                </Button>
              </Box>
            </Box>
            <Box sx={{ width: "60%", position: "relative" }}>
              {showTemplates && (
                <ListTemplates
                  hideTemplates={() => setShowTemplates(() => false)}
                  setTemplateText={(value) => setFleetMessageText(value)}
                />
              )}
              <ButtonTabs
                tabs={tabs}
                testId={TestIds.fleet.tab}
                setActiveTab={(value) => {
                  tabReset();
                  setActiveTab(value);
                }}
              />
              <Box sx={{ mt: 1 }}>{activeComponent}</Box>
            </Box>
          </Box>
          <Box sx={{ display: "flex", mt: 1, gap: 2 }}>
            <Box sx={{ width: "50%" }}>
              <Button
                variant="outlined"
                sx={{ order: { xs: 1, sm: 0 } }}
                onClick={() => closeModal()}
                aria-label={Locator.feed.input.fleetMessage.modal.cancel}
                type="button"
              >
                Cancel
              </Button>
            </Box>
            <Box sx={{ width: "50%" }}>
              <Button
                data-sb={TestIds.fleet.send}
                onClick={() => (fleetMessageContentId ? submitWithAttachment() : submitForm())}
                variant="contained"
                color="primary"
                sx={{ display: "flex", gap: 1 }}
                disabled={disabledValue}
                type="button"
                aria-label={Locator.feed.input.fleetMessage.modal.continue}
              >
                <Box>Send</Box>
                <PaperAirplane
                  style={{
                    fill: disabledValue ? theme.palette.neutral.main : theme.palette.primary.main,
                    height: "20px",
                    width: "20px",
                  }}
                />
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
